/* Movatic WMS — Frontend | CesarFilho 932048016
   DynamicForm — ADR-0039 F2: form dirigido pelo schema do OPTIONS.
   Le actions.POST/PUT da RichMetadata; renderiza inputs por type; honra visible_when
   (watch RHF, parser minimo == / !=); FK via related_endpoint (dropdown simples + teto);
   submete por apiPost/apiPut; mapeia erros 400 do backend ao campo (§2.4 backend autoritativo). */
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { apiOptions, apiGet, apiPost, apiPut } from '@/lib/api'

type FieldInfo = {
  type: string
  label?: string
  required?: boolean
  read_only?: boolean
  choices?: { value: any; display_name: string }[]
  related_endpoint?: string
  visible_when?: string
}
type Schema = Record<string, FieldInfo>

type DynamicFormProps = {
  endpoint: string
  initial?: Record<string, any> | null // ausente = criar; presente = editar
  onDone?: () => void // sucesso (fecha modal + refresca lista)
  onCancel?: () => void
}

/** Parser minimo e seguro: "campo == 'valor'" ou "campo != 'valor'".
 *  Fail-closed: qualquer coisa fora deste formato -> retorna sempre visivel. */
function evalVisibleWhen(
  expr: string | undefined,
  watchVal: (name: string) => any,
): boolean {
  if (!expr) return true
  const m = expr.match(/^\s*(\w+)\s*(==|!=)\s*'([^']*)'\s*$/)
  if (!m) return true // formato nao reconhecido -> visivel (nao esconder por engano)
  const [, field, op, value] = m
  const current = String(watchVal(field) ?? '')
  return op === '==' ? current === value : current !== value
}

export default function DynamicForm({
  endpoint,
  initial,
  onCancel,
  onDone,
}: DynamicFormProps) {
  const isEdit = !!initial
  const [schema, setSchema] = useState<Schema | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [fkOptions, setFkOptions] = useState<
    Record<string, { value: any; label: string }[]>
  >({})
  const [fkWarn, setFkWarn] = useState<Record<string, string>>({})

  const { register, handleSubmit, watch, setError, reset, formState } = useForm<
    Record<string, any>
  >({
    defaultValues: initial ?? {},
  })

  // 1) carregar o schema via OPTIONS
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const resp = await apiOptions<any>(endpoint)
        const actions = resp.data?.actions ?? {}
        const info: Schema =
          (isEdit ? actions.PUT : actions.POST) ?? actions.POST ?? {}
        if (!cancelled) {
          if (!info || Object.keys(info).length === 0) {
            setLoadErr(
              'Sem permissao de escrita neste cadastro (OPTIONS sem actions).',
            )
          } else {
            setSchema(info)
          }
        }
      } catch (e: any) {
        if (!cancelled)
          setLoadErr(e?.message || 'Erro ao carregar o formulario.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [endpoint, isEdit])

  // 1b) reaplica initial APOS o schema carregar (os campos so' existem entao); sem isto o RHF
  // nao injeta os defaults nos selects/condicionais registados tarde -> edicao mostra valores errados.
  useEffect(() => {
    if (schema && initial) reset(initial)
  }, [schema, initial, reset])

  // 2) carregar opcoes das FKs (dropdown simples + teto/aviso)
  useEffect(() => {
    if (!schema) return
    const fks = Object.entries(schema).filter(
      ([, f]) => f.type === 'fk' && f.related_endpoint && !f.read_only,
    )
    let cancelled = false
    ;(async () => {
      for (const [name, f] of fks) {
        try {
          const resp = await apiGet<any>(f.related_endpoint!, {
            params: { page_size: 100 },
          })
          const d = resp.data
          const rows: any[] = Array.isArray(d?.results)
            ? d.results
            : Array.isArray(d)
              ? d
              : []
          const count: number = d?.count ?? rows.length
          const opts = rows.map((r) => ({
            value: r.id,
            label: r.name ?? r.code ?? r.label ?? String(r.id),
          }))
          if (!cancelled) {
            setFkOptions((prev) => ({ ...prev, [name]: opts }))
            if (count > rows.length) {
              setFkWarn((prev) => ({
                ...prev,
                [name]: `A mostrar ${rows.length} de ${count} — refine.`,
              }))
            }
          }
        } catch {
          // fail-closed: FK sem opcoes -> cai para input de id (abaixo)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [schema])

  const fields = useMemo(
    () =>
      schema ? Object.entries(schema).filter(([, f]) => !f.read_only) : [],
    [schema],
  )

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit) await apiPut(`${endpoint}${initial!.id}/`, values)
      else await apiPost(endpoint, values)
      onDone?.()
    } catch (e: any) {
      const data = e?.response?.data
      if (data && typeof data === 'object') {
        // DRF 400: { campo: [mensagens] } -> mapeia ao campo (§2.4 backend autoritativo)
        let mapped = false
        for (const [field, msgs] of Object.entries(data)) {
          const msg = Array.isArray(msgs) ? String(msgs[0]) : String(msgs)
          if (field in (schema ?? {})) {
            setError(field as any, { type: 'server', message: msg })
            mapped = true
          }
        }
        if (!mapped)
          setError('root' as any, {
            type: 'server',
            message: JSON.stringify(data),
          })
      } else {
        setError('root' as any, {
          type: 'server',
          message: e?.message || 'Erro ao guardar.',
        })
      }
    }
  })

  if (loadErr) return <div className="text-sm text-red-600">{loadErr}</div>
  if (!schema)
    return (
      <div className="text-sm text-neutral-500">A carregar formulario…</div>
    )

  return (
    <form onSubmit={onSubmit} className="grid gap-3">
      {fields.map(([name, f]) => {
        if (!evalVisibleWhen(f.visible_when, watch)) return null
        const label = f.label ?? name
        const common = { id: name, className: 'input w-full' }

        let control: React.ReactNode
        if (f.type === 'boolean') {
          control = <input type="checkbox" {...register(name)} />
        } else if (f.type === 'number') {
          control = <input type="number" {...register(name)} {...common} />
        } else if (f.type === 'datetime') {
          control = (
            <input type="datetime-local" {...register(name)} {...common} />
          )
        } else if (f.type === 'select') {
          control = (
            <select {...register(name)} {...common}>
              <option value="">—</option>
              {(f.choices ?? []).map((c) => (
                <option key={String(c.value)} value={c.value}>
                  {c.display_name}
                </option>
              ))}
            </select>
          )
        } else if (f.type === 'fk') {
          const opts = fkOptions[name]
          control = opts ? (
            <select {...register(name)} {...common}>
              <option value="">—</option>
              {opts.map((o) => (
                <option key={String(o.value)} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="number"
              placeholder="id"
              {...register(name)}
              {...common}
            />
          )
        } else if (f.type === 'm2m') {
          control = (
            <div className="text-xs text-amber-600">
              Campo m2m ainda nao suportado (F4).
            </div>
          )
        } else {
          control = <input type="text" {...register(name)} {...common} />
        }

        const err = (formState.errors as any)[name]
        return (
          <div key={name} className="grid gap-1">
            <label htmlFor={name} className="text-sm">
              {label}
              {f.required ? ' *' : ''}
            </label>
            {control}
            {fkWarn[name] && (
              <span className="text-xs text-amber-600">{fkWarn[name]}</span>
            )}
            {err && <span className="text-xs text-red-600">{err.message}</span>}
          </div>
        )
      })}

      {(formState.errors as any).root && (
        <div className="text-sm text-red-600">
          {(formState.errors as any).root.message}
        </div>
      )}

      <div className="mt-2 flex gap-2 justify-end">
        <button type="button" className="btn subtle" onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" className="btn" disabled={formState.isSubmitting}>
          {isEdit ? 'Guardar' : 'Criar'}
        </button>
      </div>
    </form>
  )
}
